import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import CloseButton from "@/components/buttons/CloseButton";
import { DefaultPrimaryButton, InformationModal } from "@/styles/globalstyles";
import { useUpdatePASupplierBuyerCode } from "@/hooks/PAHooks";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { useToast } from "@/hooks/useToast";
import { getAxiosErrorMessage } from "@/utils/healper";
import type { PASupplier } from "@/api/PAService";

interface SelectBuyerCodeModalProps {
  open: boolean;
  handleClose: () => void;
  supplier: PASupplier | null;
}

const SelectBuyerCodeModal = ({
  open,
  handleClose,
  supplier,
}: SelectBuyerCodeModalProps) => {
  const { showToast } = useToast();
  const [selected, setSelected] = useState("");

  const buyerCodeOptions =
    supplier?.buyer_code
      ?.split(",")
      .map((c) => c.trim())
      .filter(Boolean) ?? [];

  useEffect(() => {
    setSelected(buyerCodeOptions[0] ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier]);

  const updateMutation = useUpdatePASupplierBuyerCode();

  useQueryHandler({
    isFetching: updateMutation.isPending,
    isError: updateMutation.isError,
    error: updateMutation.error,
    errorMessage: getAxiosErrorMessage(updateMutation.error),
  });

  const handleSave = () => {
    if (!supplier || !selected) return;
    updateMutation.mutate(
      { pa_supplier_id: supplier.pa_supplier_id, buyer_code: selected },
      {
        onSuccess: () => {
          showToast("บันทึก Buyer Code สำเร็จ", "success");
          handleClose();
        },
      },
    );
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ ...InformationModal, maxHeight: "90vh" }}>
        <CloseButton onClick={handleClose} />
        <Typography variant="h2">เลือก Buyer Code</Typography>
        <Box p={1}>
          <Divider sx={{ my: 1.5 }} />
          <Box
            sx={{
              display: "flex",
              gap: 3,
              px: 1.5,
              py: 1,
              mb: 2,
              borderRadius: 1,
              backgroundColor: "action.hover",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              PA Supplier ID: <strong>{supplier?.pa_supplier_id}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supplier Code: <strong>{supplier?.supplier_code}</strong>
            </Typography>
          </Box>
          <FormControl fullWidth>
            <InputLabel>Buyer Code</InputLabel>
            <Select
              value={selected}
              label="Buyer Code"
              onChange={(e) => setSelected(e.target.value)}
            >
              {buyerCodeOptions.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            onClick={handleSave}
            disabled={!selected}
          >
            บันทึก
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SelectBuyerCodeModal;
